import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LocalizationService } from '../internationalization/localization.service';
import { ComplianceCheckAnswerModel, InductionQnA } from '@app/models/inductionqna.model';
import { CityComplianceStatus, CityWorkType, DefaultCompanyType, DisplayedComplianceStatus, JobType } from '@app/shared/enums';
import { ConfigurationService } from '@app/services/configuration/configuration.service';
import { Job } from '@app/models/job.model';
import { SiteVisitReason } from '@app/models/sitevisitreason.model';
import { AuthService } from '@app/services/auth/auth.service';
import { User } from '@app/models/user.model';
import { UserService } from '@app/services/user/user.service';
import { environment } from 'src/environments/environment';
import { FileStorageService } from '@app/services/filestorage/filestorage.service';
import { SiteService } from '@app/services/site/site.service';
import { Site } from '@app/models/site.model';
import { FormService } from '@app/services/form/form.service';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { UploadFileStorage } from '@app/models/filestorage.model';
import { InductionConfig } from '@app/models/inductionconfig.model';
import { UtilsService } from '@app/services/util/util-service';

@Component({
  selector: 'app-visitor-card',
  templateUrl: './visitor-card.component.html',
  styleUrl: './visitor-card.component.scss',
  standalone: false
})
export class VisitorCardComponent implements OnInit {

  currentUser: User | undefined;
  fullName: string = '';
  phoneNumber: string = '';
  companyName: string = '';
  profileImgLink: string | undefined;

  currentSite: Site | undefined;
  signInDateTime: Date | undefined;

  defaultCityFMCompanyName: string = DefaultCompanyType.CITYFM;
  defaultCityFMAsiaCompanyName: string = DefaultCompanyType.CITYFMASIA;

  profileRapidGlobalRequired = false;
  objCompliance: ComplianceCheckAnswerModel | undefined;
  enumCityComplianceStatus = CityComplianceStatus;

  statusCompliant = this._localizationService.translate(DisplayedComplianceStatus.statusCompliant);
  statusNotCompliant = this._localizationService.translate(DisplayedComplianceStatus.statusNotCompliant);
  statusNotApplicable = this._localizationService.translate(DisplayedComplianceStatus.statusNotApplicable);

  siteVisitReasons: SiteVisitReason[] = [];
  allocatedJobs: Job[] = [];
  nonCityFMWork: string = CityWorkType.NONCITYWORK;

  inductionConfig: InductionConfig | undefined;
  permit: string[] = [];
  permitQnA: InductionQnA;

  constructor(private _router: Router,
    private _authService: AuthService,
    private _userService: UserService,
    private _fileStorageService: FileStorageService,
    private _siteService: SiteService,
    private _formService: FormService,
    private _jobService: JobService,
    private _localizationService: LocalizationService,
    private _configurationService: ConfigurationService,
    private _navigationService: NavigationService,
    private _utilsService: UtilsService,
  ) {
    this._navigationService.onChange(true, false);
  }

  async ngOnInit() {
    await this.loadSite();
    await this.loadUserData();
    await this.loadSignInDetails();
    await this.processSiteVisitReasons();
  }

  async loadUserData() {
    this.currentUser = await this._authService.getUser();
    if (this.currentUser) {
      this.fullName = this.currentUser.firstName + ' ' + this.currentUser.lastName;
      this.phoneNumber = this.currentUser.mobileNumber || '';
      await this.loadUserCompany();
      this.getProfileImageLink(this.currentUser.photo);
    }
  }

  async loadUserCompany() {
    const signIn = await this._userService.getSignIn();
    if (signIn && signIn.complianceCompany) {
      this.companyName = signIn.complianceCompany;
    } else {
      this.companyName = DefaultCompanyType.NO_COMPANY_SPECIFIED;
    }
  }

  private getProfileImageLink(userPhoto: string) {
    if (environment.isDemo) {
      this.profileImgLink = userPhoto;
    } else {
      // Regular expression to check if string is a valid UUID
      const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

      if (!regexExp.test(userPhoto)) {
        this.profileImgLink = userPhoto;
      } else {
        if (this.currentUser) {
          this.downloadProfileImage(userPhoto);
        } else {
          this.profileImgLink = "";
        }
      }
    }
  }

  downloadProfileImage(userPhoto: string) {
    this._fileStorageService.download(userPhoto).subscribe(
      {
        next: (r: UploadFileStorage) => {
          this.profileImgLink = `data:image/${r.fileType};base64,${r.file}`;
          if (this.currentUser) {
            this.currentUser.photo = this.profileImgLink;
            this._authService.setUser(this.currentUser);
          }

        },
        error: e => {
          if (e.error && e.error.errorCode) {
            alert(this._localizationService.translate(e.error.errorCode));
          }
          else {
            alert(this._localizationService.translate("visitor_card_validation_error_downloading_photo"));
          }
          this.profileImgLink = "";
        }
      });
  }

  async loadSignInDetails() {
    const signIn = await this._userService.getSignIn();

    if (signIn && signIn.signInSignOutId) {
      this._userService.getSignInInfo().subscribe({
        next: async r => {
          if (r) {
            if (r.signInDateTime) {
              this.signInDateTime = r.signInDateTime;
            }
            if (r.inductionQnA) {
              this.inductionConfig = JSON.parse(this._utilsService.decodeBase64String(r.inductionQnA));

              if (this.inductionConfig) {
                await this.loadComplianceCheck();
                await this.loadPermitInfo();
              }
            }
          }
        },
        error: e => {
          if (e.error && e.error.errorCode) {
            alert(this._localizationService.translate(e.error.errorCode));
          }
          else {
            alert(this._localizationService.translate('visitor_card_error_get_user_signin_info'));
          }
        }
      });
    }
  }

  async loadSite() {
    this.currentSite = await this._siteService.getSignedInSite();
  }

  async loadComplianceCheck() {
    this.profileRapidGlobalRequired = await this._configurationService.getProfileRapidGlobalRequired();

    if (this.profileRapidGlobalRequired) {
      const complianceCheckQnAs = this.inductionConfig!.inductionConfigDetails.filter(i => i.type.toLowerCase() == "compliancecheck");

      if (complianceCheckQnAs && complianceCheckQnAs.length > 0) {
        if (complianceCheckQnAs[0]?.answer) {
          this.objCompliance = JSON.parse(complianceCheckQnAs[0]?.answer);
        }
      }
    }
  }

  async loadPermitInfo() {
    if (this.currentSite && this.currentSite.isPermitRequired) {
      this.permit = await this._configurationService.getPermitKey();
      this.permitQnA = await this._formService.getPermitQnA();

      for (let detail of this.inductionConfig!.inductionConfigDetails) {

        if (detail.questions && detail.questions.length > 0) {
          for (let question of detail.questions) {

            if (question.question.caption == this.permit[0] && question.answer) {
              this.permitQnA = question;
            }

          }
        }
      }
    }
  }

  getCompliantStatus(cityComplianceStatus: number | undefined): string {
    var status = "";
    if (cityComplianceStatus) {
      switch (cityComplianceStatus) {
        case CityComplianceStatus.COMPLIANT:
          status = this.statusCompliant;
          break;
        case CityComplianceStatus.NOTCOMPLIANT:
          status = this.statusNotCompliant;
          break;
        case CityComplianceStatus.NOTAPPLICABLE:
          status = this.statusNotApplicable;
          break;
        default:
      }
    }

    return status;
  }

  async processSiteVisitReasons() {
    const reasons = await this._siteService.getVisitReasons();
    this.siteVisitReasons = reasons.filter(svr => !svr.isUnallocated);

    if (this.siteVisitReasons && this.siteVisitReasons.length > 0) {
      if (this.siteVisitReasons[0].id !== '') {
        this.getJobsByReasons();
      } else {
        this.checkNonCityJobsDisplayValue(this.siteVisitReasons);
      }
    }
  }

  private getJobsByReasons() {
    this._jobService.getJobsBySelectedReasons(this.siteVisitReasons.map((s) => s.id), this.currentSite?.id!, this.siteVisitReasons.map((s) => s.calloutReferenceNumber)).subscribe({
      next: (jobs) => {
        if (jobs) {
          this.allocatedJobs = jobs;
        }
      },
      error: e => {
        alert(this._localizationService.translate(e.error.errorCode));
      }
    });
  }

  private checkNonCityJobsDisplayValue(reasons: SiteVisitReason[]) {
    reasons.forEach(reason => {
      if (reason.calloutReferenceNumber === this.nonCityFMWork) {
        reason.calloutReferenceNumber = this._localizationService.translate("visitor_card_lbl_noncityfmwork");
      }
    });
  }

  isJobReason(): boolean {
    if (this.siteVisitReasons && this.siteVisitReasons.length > 0 && this.siteVisitReasons[0].id !== '') {
      return true;
    }
    return false;
  }

  isReactiveJob(job: Job): boolean {
    return (job.jobType == JobType.REACTIVE_JOB) ? true : false;
  }

  isPPMJob(job: Job): boolean {
    return (job.jobType == JobType.PPM_JOB) ? true : false;
  }

  closeCard() {
    this._router.navigate(['siteinductioncompleted']);
  }
}
